import type { Track, RoadProjection } from '@/world/Track';
import { Rng } from '@/core/Rng';
import { tuning, type DifficultyLevel } from '@/config/tuning';
import { clamp, shortestAngleDelta } from '@/math/angles';
import { forwardX, forwardZ, headingFromForward } from '@/math/orientation';
import { canCollect, canUse, isActive, type PowerupState } from '@/physics/powerups';

/**
 * Driver.ts — the PURE AI decision function (§5.12). Numbers in,
 * {throttle, steer, boosting, useItem} out — no three.js, no DOM —
 * deterministic and unit-testable.
 *
 * This is how driving/sports games actually power their CPU opponents: NOT
 * machine-learning, but hand-written control logic — a racing line, a pure-
 * pursuit steering controller, and a FINITE STATE MACHINE of a few clear "modes".
 * The car is always in exactly one mode, with sticky rules for switching, which
 * is what keeps it from circling / flip-flopping every frame.
 *
 *   PUSH    — behind its ball: knock it down the racing line (centre, cut a touch
 *             inside bends). Aim a point ahead on the line (look further ahead the
 *             faster it goes — M12) and pure-pursue the ball's lane, so the nose
 *             strikes the ball down-course and never circles it. With an empty
 *             item slot it also bends gently toward a nearby item box on the way.
 *   RECOVER — overshot (level with / past the ball): brake, then reverse STRAIGHT
 *             back up-course to open a run-up behind the ball, then return to PUSH.
 *             (Steering inverts while reversing — handled explicitly below.) If a
 *             reverse can't get behind the ball within `maxReverseTime` (e.g. a
 *             wall behind), it pops back to PUSH and re-approaches forward instead.
 *
 * Items (M12): the AI plays by the SAME rules as the player — it earns items
 * from boxes, holds one, and fires it when the moment fits (boost on straights,
 * magnet into bends near its ball, kick as a long pass, dozer when a rival is
 * square ahead, shield when one is breathing down its neck). It only DECIDES
 * here (`useItem` in the output) — the race layer owns the slot + the effects,
 * exactly like the player's button press.
 *
 * Difficulty (`ctx.difficulty`) scales speed / aim-error / item decisiveness via
 * `tuning.ai.levels`; the modes are the same at every level.
 *
 * Steering is WORLD-space (not screen-flipped — that's player-only, §6.2).
 */

export type DriverState = 'PUSH' | 'RECOVER';

export interface DriverCar {
  x: number;
  z: number;
  heading: number;
  vx: number;
  vz: number;
}

export interface DriverBall {
  x: number;
  z: number;
  /** Ball velocity (used for matched-speed approach; optional for tests). */
  vx?: number;
  vz?: number;
}

export interface DriverMemory {
  rng: Rng;
  /** Current finite-state-machine mode. */
  state: DriverState;
  /** Seconds in the current mode (drives the anti-chatter dwell gates). */
  stateTimer: number;
  /** Seconds spent in the current RECOVER (for the give-up-and-retry timeout). */
  recoverTimer: number;
  /** Slowly-wandering aim error (units), so the rival isn't a perfect machine. */
  aimWander: number;
  /** Furthest BALL distance reached so far (for the progress watchdog). */
  bestDist: number;
  /** Seconds the ball has sat un-advancing while the car is right against it. */
  noProgress: number;
  /** Seconds a usable item has sat in the slot (drives the stale-item dump). */
  heldTime: number;
  /** Where the last watchdog stall happened (ball arc), for repeat detection. */
  stuckArc: number;
  /** Consecutive watchdog stalls at ~the same spot (M17 pocket escalation). */
  stuckRepeats: number;
  /** Start-scrum maneuver phase (M17): 0 swing · 1 approach · 2 charge. */
  scrumPhase: number;
  /** Seconds in the current scrum phase (dwell gating — no chattering). */
  scrumT: number;
  /** Seconds the scrum swing has been jammed against something. */
  scrumJam: number;
  /** Seconds the generic K-turn has been jammed (reversing into a wall). */
  kjam: number;
  /** Seconds left in the current harass swipe at the leader's ball (M77). */
  harassT: number;
  /** Cooldown until the next harass attempt may start (M77). */
  harassCd: number;
  /** Chase-back latch (M84): a started chase runs to completion (hysteresis). */
  chase: boolean;
  /** Seconds spent on the throttle without moving (M143c wedge watchdog)… */
  wedge: number;
  /** …and seconds left of the back-out it triggers. */
  backOut: number;
  /** Seconds RECOVER has been backing up without moving (M143c). */
  revJam: number;
}

export function createDriverMemory(seed: number): DriverMemory {
  return {
    rng: new Rng(seed),
    state: 'PUSH',
    stateTimer: 0,
    recoverTimer: 0,
    aimWander: 0,
    bestDist: -1,
    noProgress: 0,
    heldTime: 0,
    stuckArc: -1e9,
    stuckRepeats: 0,
    scrumPhase: 0,
    scrumT: 0,
    scrumJam: 0,
    kjam: 0,
    harassT: 0,
    harassCd: 0,
    chase: false,
    wedge: 0,
    backOut: 0,
    revJam: 0,
  };
}

export function resetDriverMemory(mem: DriverMemory): void {
  mem.state = 'PUSH';
  mem.stateTimer = 0;
  mem.recoverTimer = 0;
  mem.aimWander = 0;
  mem.bestDist = -1;
  mem.noProgress = 0;
  mem.heldTime = 0;
  mem.stuckArc = -1e9;
  mem.stuckRepeats = 0;
  mem.scrumPhase = 0;
  mem.scrumT = 0;
  mem.scrumJam = 0;
  mem.kjam = 0;
  mem.harassT = 0;
  mem.harassCd = 0;
  mem.wedge = 0;
  mem.backOut = 0;
  mem.revJam = 0;
}

export interface DriverContext {
  /** Is this AI behind the player? (Informational; rubber uses gapToPlayer.) */
  trailingPlayer: boolean;
  /** Signed ball-progress gap to the player (positive = this racer is behind).
   *  Drives the per-level rubber-band cruise adjustment. */
  gapToPlayer?: number;
  /** The nearest rival car just ahead (for the dozer decision). */
  rivalAhead?: { gap: number; lateral: number };
  /** The nearest rival car close behind (for the shield decision). */
  rivalBehind?: { gap: number; lateral: number };
  /** The nearest available item box down-course (for box-seeking). */
  boxAhead?: { ahead: number; lateral: number } | null;
  /** The nearest rival hazard (mud puddle / free-kick wall) ahead in the lane
   *  (M77) — levels with `avoidHazards` steer the aim point around it. */
  hazardAhead?: { ahead: number; lateral: number; halfSpan: number } | null;
  /** The leading human's ball, when it sits just ahead of this car (M77) —
   *  levels with `harass` may swipe it off-line in passing. */
  preyBall?: { x: number; z: number; ahead: number; lateral: number } | null;
  /** An EXTERNAL boost effect is live on this car (M104: a boost pad's
   *  padBoost surge — the sim knows, the powerup slot doesn't). Item boost +
   *  pibe are read off `power` directly; this flag covers the rest so the
   *  speed controller can actually CASH the surge (see boostCruiseScale). */
  boosted?: boolean;
  /** Which difficulty level to drive at (defaults to tuning.ai.defaultLevel). */
  difficulty?: DifficultyLevel;
  /** Per-racer pace multiplier on cruise speed (M14 field skill; default 1). */
  paceScale?: number;
  /**
   * The AI is COVERING A PLAYER'S SEAT (M143c): a player who dropped out, whose
   * car the sim hands to this driver. That car keeps the player's own engine,
   * and the player's brakes and reverse are roughly three times the AI's (the
   * M31/M39 driving-feel buffs). Every recovery move here (K-turn, swing, the
   * regulated back-up) is timed around the AI's slow, self-limiting reverse; on
   * the player's engine they overshoot and never converge (measured: such a
   * car cycled its swing at 12 to 18 units a second in reverse for minutes).
   * When this is set, brake and reverse commands are scaled so the car answers
   * as the AI's own would. Bots never set it. The value is the car's real
   * parameters.
   */
  cover?: { brake: number; reverseAccel: number; reverseTop: number; reverseEngage: number };
  /**
   * Imperfection level, 0..1 (default 1 = full built-in error). Tests pass 0 to
   * get the AI's clean, deterministic intent with no aim-wander or steering
   * noise — item decisions then fire the instant their conditions fit.
   */
  noiseAmplitude?: number;
}

export interface DriverOutput {
  throttle: number;
  steer: number;
  boosting: boolean;
  /** Fire the held item this step (the race applies it, same as a button press). */
  useItem: boolean;
}

type AiTuning = typeof tuning.ai;
type LevelTuning = AiTuning['levels'][DifficultyLevel];

/**
 * M83 BIG KID escort pacing (levels with `escort: true`): the cruise scale is
 * a straight ramp on the signed ball-progress gap to the leading human
 * (positive = this racer is BEHIND the kid). At/ahead of the kid → crawlScale
 * (dawdle — any child outpaces it, so the kid usually wins); dropped
 * riseRange behind → chaseScale (full cruise + a bonus, so a kid in a big
 * lead visibly gets hunted). The pack therefore settles just BEHIND the
 * kid's ball wherever the kid's own pace pins it on the ramp — competitive
 * at every child's skill, never a runaway in either direction. Pure, exported
 * for tests.
 */
export function escortCruiseScale(
  gap: number,
  esc: { crawlScale: number; chaseScale: number; riseRange: number },
): number {
  const t = clamp(gap / esc.riseRange, 0, 1);
  return esc.crawlScale + (esc.chaseScale - esc.crawlScale) * t;
}

/** Everything the modes need, computed once per frame. */
interface Situation {
  cp: RoadProjection;
  bp: RoadProjection;
  ball: DriverBall;
  /** Forward velocity along the heading (<0 = actually rolling backward). */
  vf: number;
  /** Down-course gap: ball arc-length − car arc-length (>0 = ball ahead, good). */
  gap: number;
  /** Signed sharpest bend within cornerLookahead (+ = bends toward +X). */
  bend: number;
  /** Racing-line lateral offset at the ball (centre, cut inside the bend). */
  racingLat: number;
  /** Half-road usable margin for aim points. */
  margin: number;
  /** Heading error vs the down-course tangent (RECOVER orients to this while braking). */
  downCourseErr: number;
}

export function decideDrive(
  track: Track,
  car: DriverCar,
  ball: DriverBall,
  ballHint: number,
  carHint: number,
  mem: DriverMemory,
  power: PowerupState,
  ctx: DriverContext,
  dt: number,
): DriverOutput {
  const ai = tuning.ai;
  const lvl = ai.levels[ctx.difficulty ?? (ai.defaultLevel as DifficultyLevel)];
  const imperfection = ctx.noiseAmplitude ?? 1; // 0 in tests → perfect & deterministic

  // --- Read the situation -----------------------------------------------------
  const cp = track.project(car.x, car.z, carHint);
  const bp = track.project(ball.x, ball.z, ballHint);
  const speed = Math.hypot(car.vx, car.vz);
  const vf = car.vx * forwardX(car.heading) + car.vz * forwardZ(car.heading);
  const margin = track.halfWidth - 2.5;
  const gap = bp.distance - cp.distance;

  // Sharpest bend within cornerLookahead, sampled at a few points so a corner
  // that builds gradually is read early; the racing line cuts a little inside it.
  let bend = 0;
  for (const frac of [0.4, 0.7, 1.0]) {
    const d = Math.min(bp.distance + ai.cornerLookahead * frac, track.length);
    const b = shortestAngleDelta(bp.dir, track.sampleAt(d).dir);
    if (Math.abs(b) > Math.abs(bend)) bend = b;
  }
  const racingLat = clamp(
    Math.sign(bend) * Math.min(Math.abs(bend) * ai.insideLineBias, ai.insideLineBiasMax),
    -margin,
    margin,
  );

  const sit: Situation = {
    cp,
    bp,
    ball,
    vf,
    gap,
    bend,
    racingLat,
    margin,
    downCourseErr: shortestAngleDelta(car.heading, cp.dir),
  };

  // --- Aim wander (built-in error; off in tests) ------------------------------
  if (imperfection > 0) {
    const target = (mem.rng.next() * 2 - 1) * lvl.aimWanderAmp * imperfection;
    mem.aimWander += (target - mem.aimWander) * Math.min(ai.aimWanderRate * dt, 1);
  } else {
    mem.aimWander = 0;
  }

  // --- Progress watchdog ------------------------------------------------------
  // Count seconds the ball sits un-advancing WHILE the car is right near it (by
  // ACTUAL distance, so circling a stopped ball counts as stuck — a rolled-ahead
  // ball the car is chasing does not, since the car is far from it).
  const carBallDist = Math.hypot(car.x - ball.x, car.z - ball.z);
  // M143c: ...or the car is LEVEL with a ball at rest across the road (see
  // tuning.ai.stuck). That is a stall too, and proximity alone never saw it.
  const lateralOff = Math.abs(cp.lateral - bp.lateral);
  const levelStall =
    lateralOff > ai.stuck.levelLateral &&
    gap < ai.stuck.levelAhead &&
    gap > -ai.recoverDist &&
    Math.hypot(ball.vx ?? 0, ball.vz ?? 0) < ai.stuck.restSpeed;
  if (bp.distance > mem.bestDist + 0.5) {
    mem.bestDist = bp.distance;
    mem.noProgress = 0;
    // Genuinely clear of the last stall spot → the pocket is beaten (M17).
    if (mem.stuckRepeats > 0 && bp.distance > mem.stuckArc + ai.stuck.sameSpot) {
      mem.stuckRepeats = 0;
    }
  } else if (carBallDist < ai.recoverDist || levelStall) {
    mem.noProgress += dt;
  } else {
    mem.noProgress = 0;
  }

  // --- Finite state machine: pick the mode, then act --------------------------
  transition(mem, sit, ai, dt);

  // Rubber-band, ASYMMETRIC per level (M15): trailing the player pulls
  // `catchUp` assist (so a passed rival keeps hunting you); leading eases off
  // by `mercy` (legendary shows none — it just drives away).
  // M83 BIG KID: `escort` levels skip the linear rubber entirely — their pace
  // IS the gap to the kid's ball (escortCruiseScale): dawdle at/ahead so any
  // child outpaces them, ramp up when dropped behind so a runaway kid still
  // gets hunted. catchUp/mercy are ignored on escort levels.
  let cruiseScale: number;
  if (lvl.escort) {
    cruiseScale = escortCruiseScale(ctx.gapToPlayer ?? 0, ai.escort) * (ctx.paceScale ?? 1);
  } else {
    const gapSigned = clamp((ctx.gapToPlayer ?? 0) / ai.rubberRange, -1, 1);
    const rubber = gapSigned > 0 ? lvl.catchUp : lvl.mercy;
    cruiseScale = (1 + gapSigned * rubber * ai.rubberMax) * (ctx.paceScale ?? 1);
  }

  // Box-seeking (M12): with an empty slot, bend the dribble line gently toward a
  // nearby item box — but never far off the ball's lane (the ball stays boss).
  let boxLat: number | null = null;
  const box = ctx.boxAhead;
  if (
    box &&
    canCollect(power) &&
    mem.state === 'PUSH' &&
    box.ahead < Math.min(ai.items.boxSeekAhead, Math.max(gap, 0) + 25) &&
    Math.abs(box.lateral - bp.lateral) < ai.items.boxSeekMaxOff
  ) {
    boxLat = clamp(box.lateral, -margin, margin);
  }

  let out: { throttle: number; steer: number };
  // The real-space maneuver runs in two pathological cases the arc-length
  // machinery can't solve: (a) car at/past a ball pinned at the start
  // boundary, and (b) repeated watchdog stalls where the car sits BESIDE the
  // ball (RECOVER's along-track reverse can't fix a lateral offset), with a
  // hard fallback after further repeats. It stays a LAST resort — for routine
  // scrappy corners plain RECOVER is faster (measured: eager escalation cost
  // hard ~9s on clean runs). An ordinary grid start is plain PUSH territory.
  // Strictly the telemetry-proven catastrophic signatures — for ordinary
  // hairpin pockets the grind of RECOVER cycles is slower but safer (measured:
  // letting the maneuver loose there traded one stall for another). The third
  // signature (M17b): the ball REGRESSING far below its best while the car is
  // engaged — hairpin walls banking every strike back up-course marched balls
  // 150-400 m backwards in traces; the maneuver's down-course-only charge
  // guard is the cure, and repeats reset on real progress hands control back.
  const ballRegressed = mem.bestDist - sit.bp.distance > 30;
  const needManeuver =
    (sit.bp.distance < ai.scrumZone && sit.gap < 4) ||
    (mem.stuckRepeats >= ai.stuck.escalateAfter &&
      carBallDist < ai.stuck.maneuverDist &&
      (lateralOff > 4 || ballRegressed));
  const scrumOut = needManeuver ? scrumManeuver(track, car, sit, mem, ai, dt) : null;

  // M77 harass bookkeeping (ticks whether or not a swipe is running).
  mem.harassCd = Math.max(0, mem.harassCd - dt);
  const prey = ctx.preyBall;
  const canHarass =
    lvl.harass &&
    prey != null &&
    mem.state === 'PUSH' &&
    sit.gap > ai.harass.minOwnGap && // mid-chase: own ball punted well ahead
    prey.ahead > 2 &&
    prey.ahead < ai.harass.range &&
    Math.abs(prey.lateral - sit.cp.lateral) < ai.harass.maxLateral;
  if (mem.harassT > 0) {
    mem.harassT = canHarass ? mem.harassT - dt : 0; // conditions broke → abort
    if (mem.harassT <= 0) mem.harassCd = ai.harass.cooldown;
  } else if (canHarass && mem.harassCd <= 0 && !needManeuver) {
    mem.harassT = ai.harass.time;
  }

  // M84: chase-back runs on a HYSTERESIS latch — enter past enterGap, release
  // only once the ball is nearly ahead again (exitGap). M77 re-checked the raw
  // enterGap every tick, and a ball dying EXACTLY at the threshold (measured:
  // dead ball 35 u behind, NYC legendary) pinned the car in a one-tick
  // PUSH-creep / K-turn-reverse limit cycle AT the boundary, forever. Latched,
  // a started chase always completes its U-turn + drive to the staging point.
  mem.chase = sit.gap < (mem.chase ? ai.chase.exitGap : ai.chase.enterGap);

  // M104 — a live boost effect (item boost, the pibe golden run, or a boost
  // pad's surge via ctx.boosted) raises the CRUISE TARGET the P-controller
  // chases. Without this the controller kept targeting the plain cruise
  // number, so a boosting bot gained ~3 u/s where a boosting human gains ~28
  // (measured — the two biggest items in the game were near no-ops for bots).
  const boosted = isActive(power, 'boost') || isActive(power, 'pibe') || ctx.boosted === true;

  if (scrumOut) {
    out = scrumOut;
  } else if (mem.chase) {
    // M77 CHASE-BACK: the ball is far BEHIND the car (his studs/walls/kicks, a
    // hairpin ricochet, a scorpion landing). Reversing there is the measured
    // 30-110 s fade — TURN AROUND and drive forward to a point behind it.
    out = chaseBack(track, car, sit, lvl, ai, cruiseScale, boosted, dt, mem);
    mem.state = 'PUSH'; // re-engage cleanly once the ball is ahead again
    mem.stateTimer = 0;
  } else if (mem.harassT > 0 && prey) {
    // M77 HARASS: swipe the leading human's ball off-line in passing — aim
    // THROUGH it (the scrum-charge extension trick) at full throttle. The
    // strike itself is plain collision physics; Legend's ballPush does the rest.
    const d = Math.hypot(prey.x - car.x, prey.z - car.z) || 1;
    const exX = prey.x + ((prey.x - car.x) / d) * 5;
    const exZ = prey.z + ((prey.z - car.z) / d) * 5;
    const err = shortestAngleDelta(car.heading, headingFromForward(exX - car.x, exZ - car.z));
    out = { throttle: 1, steer: clamp(err * ai.steerGain * 1.4, -1, 1) };
  } else if (mem.state === 'RECOVER') {
    out = recoverBehavior(sit, ai);
  } else {
    out = pushBehavior(
      track, car, sit, lvl, ai, mem, imperfection, cruiseScale, boosted, speed, boxLat, ctx, dt,
    );
  }

  // --- Item decisions (M12) ----------------------------------------------------
  // The SAME earn-and-fire rules as the player: only a held, revealed item can
  // fire, one effect at a time. `itemAggression` (per level) is how decisively
  // the AI pulls the trigger once the moment fits; after `staleItemTime` it
  // dumps the item rather than blocking its slot forever.
  const ballErr = Math.abs(bp.lateral - racingLat);
  let useItem = false;
  if (canUse(power)) {
    mem.heldTime += dt;
    const it = ai.items;
    const pw = tuning.powerups;
    const stale = mem.heldTime > pw.staleItemTime;
    const inBallRange = carBallDist < pw.ballRange;
    let want = false;
    switch (power.held) {
      case 'boost':
        want =
          stale ||
          (mem.state === 'PUSH' &&
            Math.abs(bend) < it.boostMaxCorner &&
            speed > it.boostMinSpeed &&
            ballErr < it.boostMaxBallErr &&
            gap > 1);
        break;
      case 'magnet':
        // Carry the ball through an upcoming bend (or just stop sitting on it).
        // M104: a STALE ball item fires on ANY in-range touch — the old
        // `state === PUSH && gap > 0` prefix could dead-lock the slot for the
        // whole race after a knock-back (RECOVER/chase-back never satisfy it,
        // and canCollect stays false so no new box either).
        want =
          (stale && inBallRange) ||
          (mem.state === 'PUSH' && inBallRange && gap > 0 && Math.abs(bend) > it.magnetMinBend);
        break;
      case 'kick':
        // A long pass down a straight, cleanly lined up (or a stale punt —
        // M104: any in-range moment, see the magnet note).
        want =
          (stale && inBallRange) ||
          (mem.state === 'PUSH' &&
            inBallRange &&
            gap > 0 &&
            ballErr < it.kickMaxBallErr &&
            Math.abs(bend) < it.kickMaxCorner);
        break;
      case 'dozer':
        want =
          stale ||
          (ctx.rivalAhead !== undefined &&
            ctx.rivalAhead.gap < it.dozerMaxGap &&
            Math.abs(ctx.rivalAhead.lateral) < it.dozerMaxLateral &&
            speed > it.dozerMinSpeed);
        break;
      case 'shield':
        want = stale || (ctx.rivalBehind !== undefined && ctx.rivalBehind.gap < it.shieldMaxGap);
        break;
      case 'tackle':
        // A homing item — send it promptly (the race layer resolves the leader).
        want = stale || mem.heldTime > 0.8;
        break;
      case 'wall':
        // Defensive, like the shield: plant it when someone's on my tail.
        want = stale || (ctx.rivalBehind !== undefined && ctx.rivalBehind.gap < it.shieldMaxGap);
        break;
      case 'offside':
        // Hits EVERYONE ahead — worth it almost immediately when trailing.
        want = stale || (ctx.trailingPlayer && mem.heldTime > 0.5);
        break;
      case 'pibe':
        // Golden — release it fast and enjoy the run (it's never a bad time).
        want = stale || mem.heldTime > 0.6;
        break;
      case 'studs':
        // Spikes shred anyone you touch — deploy them in traffic (a rival
        // just ahead or right on your tail), else hold for the moment.
        want =
          stale ||
          (ctx.rivalAhead !== undefined && ctx.rivalAhead.gap < it.dozerMaxGap) ||
          (ctx.rivalBehind !== undefined && ctx.rivalBehind.gap < it.shieldMaxGap);
        break;
      case 'scorpion':
        // A comeback loft — fire it on a straight with the ball cleanly at the
        // nose (the sim gates it on ball range), like a kick. M104: stale =
        // any in-range moment (see the magnet note).
        want =
          (stale && inBallRange) ||
          (mem.state === 'PUSH' &&
            inBallRange &&
            gap > 0 &&
            ballErr < it.kickMaxBallErr &&
            Math.abs(bend) < it.kickMaxCorner);
        break;
      case 'mud':
        // Drop it to gum up a chaser — defensive, like the wall/shield.
        want = stale || (ctx.rivalBehind !== undefined && ctx.rivalBehind.gap < it.shieldMaxGap);
        break;
    }
    if (want) {
      // Clean intent (tests) fires immediately; live AI pulls the trigger with a
      // per-second decisiveness scaled by its level (stale always fires).
      useItem =
        imperfection === 0 || stale || mem.rng.next() < lvl.itemAggression * 3 * dt;
    }
  } else {
    mem.heldTime = 0;
  }

  // --- Wedge watchdog (M143c) --------------------------------------------------
  // On the throttle and going nowhere: pinned against something no other rule
  // here knows about (see tuning.ai.wedge). Back straight out, then try again.
  let throttle = clamp(out.throttle, -1, 1);
  let steer = clamp(out.steer, -1, 1);
  // The mirror case: RECOVER backing up against something solid. Latch the
  // chase-back, which turns the car round and drives it to its ball (the
  // latch holds until the ball is ahead again, see mem.chase above).
  if (mem.state === 'RECOVER' && throttle < -0.3 && Math.abs(vf) < ai.wedge.speed) {
    mem.revJam += dt;
    if (mem.revJam >= ai.wedge.reverseAfter) {
      mem.revJam = 0;
      mem.chase = true;
    }
  } else {
    mem.revJam = 0;
  }
  if (mem.backOut > 0) {
    mem.backOut -= dt;
    mem.wedge = 0;
    throttle = -ai.wedge.reverse;
    steer = 0;
  } else if (throttle > 0.5 && speed < ai.wedge.speed) {
    mem.wedge += dt;
    if (mem.wedge >= ai.wedge.after) {
      mem.wedge = 0;
      mem.backOut = ai.wedge.backFor;
    }
  } else {
    mem.wedge = 0;
  }

  // --- Covering a player's seat (M143c, see DriverContext.cover) ----------------
  // Scale a brake or reverse command so the player's much stronger engine
  // answers like the AI's own: same braking, same reverse pull, same taper to
  // the AI's low reverse top speed.
  if (ctx.cover && throttle < 0) {
    const c = ctx.cover;
    if (vf > c.reverseEngage) {
      throttle *= Math.min(1, ai.brake / c.brake);
    } else {
      const back = Math.max(0, -vf);
      const want = ai.reverseAccel * Math.max(0, 1 - back / ai.reverseTop);
      const have = c.reverseAccel * Math.max(1e-6, 1 - back / c.reverseTop);
      throttle *= Math.min(1, want / have);
    }
  }

  return {
    throttle,
    steer,
    boosting: isActive(power, 'boost'),
    useItem,
  };
}

/**
 * The state-machine transition. Mutates `mem.state` and the dwell timers. Kept
 * separate from behavior so it's easy to reason about and unit-test. Switching
 * needs a clear margin AND a minimum dwell (anti-chatter) — the Schmitt-trigger
 * that stops the per-frame flip-flopping.
 */
function transition(mem: DriverMemory, sit: Situation, ai: AiTuning, dt: number): void {
  const f = ai.fsm;
  let next = mem.state;

  // M17: inside the start-line scrum zone there is no room BEHIND the ball, so
  // a brake-and-reverse recovery can never succeed (this was the death loop
  // that froze rivals for entire races in the owner's telemetry). Stay in PUSH
  // and keep striking instead.
  const canRecover = sit.bp.distance > ai.scrumZone;

  if (mem.noProgress > ai.progressTimeout) {
    // Watchdog (highest priority): genuinely stuck near the ball → back up & retry.
    mem.noProgress = 0;
    // Track repeat stalls at ~the same spot (drives the pocket escalation).
    if (Math.abs(sit.bp.distance - mem.stuckArc) < ai.stuck.sameSpot) mem.stuckRepeats += 1;
    else mem.stuckRepeats = 1;
    mem.stuckArc = sit.bp.distance;
    if (canRecover) next = 'RECOVER';
  } else if (mem.state === 'PUSH') {
    // Leave PUSH only when the car has genuinely OVERSHOT (ball no longer ahead).
    if (mem.stateTimer >= f.minPushDwell && sit.gap < f.enterRecoverLead && canRecover) {
      next = 'RECOVER';
    }
  } else {
    // RECOVER: brake & reverse straight back until the ball is clearly ahead again,
    // then re-approach (the car can turn tightly enough to reach it once it's ahead).
    if (mem.stateTimer >= f.minRecoverDwell && sit.gap > f.exitRecoverLead) next = 'PUSH';
    else if (mem.recoverTimer > f.maxReverseTime) next = 'PUSH';
  }

  if (next !== mem.state) {
    mem.state = next;
    mem.stateTimer = 0;
    if (next === 'RECOVER') mem.recoverTimer = 0;
  } else {
    mem.stateTimer += dt;
    if (mem.state === 'RECOVER') mem.recoverTimer += dt;
  }
}

/** PUSH — pure-pursuit along the ball's lane: curve onto the ball's lateral and
 *  drive through it down-course (arrives BEHIND the ball, not beside it). */
function pushBehavior(
  track: Track,
  car: DriverCar,
  sit: Situation,
  lvl: LevelTuning,
  ai: AiTuning,
  mem: DriverMemory,
  imperfection: number,
  cruiseScale: number,
  boosted: boolean,
  speed: number,
  boxLat: number | null,
  ctx: DriverContext,
  dt: number,
): { throttle: number; steer: number } {
  // Aim a point ahead of the CAR, on the BALL's lateral (eased a little toward
  // the racing line + the aim wander; bent toward a nearby item box when the
  // slot is empty). Pure-pursuing it curves the car onto the ball's lane early,
  // so it arrives behind the ball and pushes it down-course — instead of
  // approaching straight and ending up beside it.
  // High levels actively steer the BALL toward the racing line (per-level
  // `line` override); lower levels mostly push it down whatever lane it's in.
  const lineCorrect = lvl.line ?? ai.lineCorrect;
  let aimLat = clamp(
    sit.bp.lateral + (sit.racingLat - sit.bp.lateral) * lineCorrect + mem.aimWander,
    -sit.margin,
    sit.margin,
  );
  if (boxLat !== null) {
    aimLat = clamp(aimLat + (boxLat - aimLat) * ai.items.boxSeekBlend, -sit.margin, sit.margin);
  }
  // M77 hazard sense (legendary): a rival mud puddle / free-kick wall sits in
  // the lane ahead — shift the aim point past its clear edge (prefer the side
  // with more road). Dumb AI ate every puddle the owner dropped.
  const hz = ctx.hazardAhead;
  if (lvl.avoidHazards && hz && hz.ahead > 0 && hz.ahead < ai.hazard.lookahead) {
    const clear = hz.halfSpan + ai.hazard.margin;
    if (Math.abs(aimLat - hz.lateral) < clear) {
      const side = hz.lateral > 0 ? -1 : 1; // dodge toward the wider side
      aimLat = clamp(hz.lateral + side * clear, -sit.margin, sit.margin);
    }
  }
  // M12: the pursuit point slides further ahead the faster we go — a fixed short
  // look-ahead made the chase fishtail at the new (much higher) cruise speeds.
  const lookAhead = ai.pushLookAheadBase + ai.pushLookAheadPerSpeed * speed;
  const aimS = track.sampleAt(Math.min(sit.cp.distance + lookAhead, track.length));
  const aimX = aimS.x + aimS.rightX * aimLat;
  const aimZ = aimS.z + aimS.rightZ * aimLat;

  const desired = headingFromForward(aimX - car.x, aimZ - car.z);
  const steerErr = shortestAngleDelta(car.heading, desired);

  // Three-point turn (M17): the aim point is far behind the nose and we're
  // slow — forward pursuit would only orbit it (the point sits inside the
  // minimum turning circle). Back up while counter-steering to swing the nose
  // around in place; normal pursuit resumes as soon as it comes off full lock.
  // (Reverse steering inverts in arcadeCar, hence the opposite steer sign.)
  // Jam guard: cornered against a wall, reverse achieves nothing forever —
  // bail to forward pursuit until genuinely moving again (measured deadlock).
  const wantKturn = Math.abs(steerErr) > ai.kturn.minErr && Math.abs(sit.vf) < ai.kturn.maxSpeed;
  if (wantKturn && mem.kjam <= 0.6) {
    if (Math.abs(sit.vf) < 0.6) mem.kjam += dt;
    else mem.kjam = 0;
    return { throttle: -ai.kturn.reverse, steer: -Math.sign(steerErr) };
  }
  if (!wantKturn) mem.kjam = 0;

  const noise = imperfection > 0 ? lvl.steerNoise * imperfection * (mem.rng.next() * 2 - 1) : 0;
  const steer = steerErr * ai.steerGain + noise;

  // Drive toward cruise speed, eased for the upcoming bend and for hard steering
  // (so a tight low-speed turn radius is available to line up, not circle). Pace
  // FORWARD velocity (vf), not |speed| — otherwise, entering PUSH while still
  // rolling backward, it would read "going fast" and brake itself into more reverse.
  // `cornerTrust` (per-level): brake LESS for bends — but ONLY while the ball
  // is actually cradled on the nose (that's when the cradle physics can hold
  // it through the corner). A LOOSE ball at a bend still gets the full brake:
  // trusting corners while chasing was measured to pinball (seed-42 thrash).
  const cradled = sit.gap > 0 && sit.gap < ai.touchGap + 2.5;
  const cornerEase = cradled ? (lvl.cornerTrust ?? ai.cornerEase) : ai.cornerEase;
  // M83 BIG KID: while an escort level is easing off (cruiseScale < 1), its
  // speed FLOORS ease with it — cornerSpeed and strikeMin both sit well above
  // a true crawl, and a floored "crawl" still outran a slow child.
  const escortFloor = lvl.escort ? clamp(cruiseScale, 0, 1) : 1;
  // M104: a live boost (item/pibe/pad) raises the cruise TARGET so the
  // P-controller commands real throttle into the boost engine — corner ease
  // still applies, so a boosting bot brakes for bends like everyone else.
  const cruise = lvl.cruiseSpeed * (boosted ? ai.boostCruiseScale : 1);
  let speedCap = Math.max(
    lvl.cornerSpeed * escortFloor,
    cruise * cruiseScale * (1 - Math.min(Math.abs(sit.bend) * cornerEase, 1)),
  );

  // PUNT mode (M14): on a long-enough straight with the ball on the line,
  // punt-capable levels skip the polite approach cap and hit the ball FLAT OUT
  // — it rockets ahead, they chase at top speed and hit it again (the optimal
  // human technique the owner uses). Bends fall back to the dribble below.
  const ballErr = Math.abs(sit.bp.lateral - sit.racingLat);
  const punting =
    lvl.punt &&
    sit.gap > 0 &&
    sit.gap < ai.punt.window &&
    ballErr < ai.punt.maxBallErr &&
    puntStraight(track, sit.bp.distance, ai.punt);

  // APPROACH control (M12): closing on the ball, cap to "ball speed + a closing
  // allowance that shrinks with the gap" — arrive matched, touch, accelerate
  // together. This is what killed the RECOVER thrash at the new high cruises.
  // `strikeMin` floors it: a slow / wall-stuck / returning ball still gets a
  // solid knock (a pure matched approach tiptoed forever behind a dead ball).
  // M77: coming in hotter under catch-up rubber needs a proportionally longer
  // braking runway — a fixed window measured as overshoot→RECOVER churn.
  const approachWindow = ai.approachWindow * Math.max(1, cruiseScale);
  if (!punting && sit.gap < approachWindow) {
    const ballFwd = Math.max(
      0,
      (sit.ball.vx ?? 0) * forwardX(sit.bp.dir) + (sit.ball.vz ?? 0) * forwardZ(sit.bp.dir),
    );
    // M77 hairpin guard: soften the strike floor in sharp bends — full-force
    // hairpin knocks bank the ball off the walls and up-course (measured:
    // Patagonia solo 134→184 s without this). M104: applies to EVERY level
    // now, not just lively-ball ones — the faster touchSpeed dribble made
    // MEDIUM ping-pong Patagonia's hairpins too (measured: a 42 s wall-pocket
    // stall, 147→166 s, gone with the guard universal).
    const strikeMin =
      Math.abs(sit.bend) > ai.hairpinBend
        ? ai.strikeMin * ai.hairpinStrikeScale
        : ai.strikeMin;
    const approachCap = Math.max(
      strikeMin,
      ballFwd + ai.touchSpeed + ai.approachGain * Math.max(0, sit.gap - ai.touchGap),
    );
    speedCap = Math.min(speedCap, approachCap);
  }

  // M83 BIG KID: the escort crawl caps the CHASE, never the KNOCK. The
  // approach block above can only LOWER speedCap, so under a crawl cruise
  // (~8 u/s) strikeMin's "a dead ball still gets a solid knock" never
  // engages — measured as a permanent wall-pocket stall (seed 3, dist 192:
  // every 8 u/s nudge banked the ball straight back). A dawdling escort car
  // therefore winds up to the full strike exactly when the ball sits nearly
  // dead just ahead; the moment it's rolling again the crawl cap returns.
  if (lvl.escort && sit.gap > 0 && sit.gap < 14) {
    const ballSpeed = Math.hypot(sit.ball.vx ?? 0, sit.ball.vz ?? 0);
    if (ballSpeed < 4) speedCap = Math.max(speedCap, ai.strikeMin);
  }

  const turnEase = clamp(1 - Math.abs(steerErr) / ai.hardSteer, ai.minSpeedFrac, 1);
  const throttle = (speedCap * turnEase - sit.vf) * ai.speedGain;
  return { throttle, steer };
}

/**
 * CHASE-BACK (M77) — the ball is far behind the car (gap < chase.enterGap).
 * The old behavior was RECOVER's regulated reverse (~14 u/s) interleaved with
 * PUSH flip-flops: net ~9 u/s backward, measured 30-110 s lost per incident in
 * the owner's Legend traces — THE structural mid-race fade. A human U-turns
 * and drives. So: aim a staging point BEHIND + BESIDE the ball (the proven
 * scrum-maneuver geometry — never ram it deeper on the way in), K-turn when
 * the nose points the wrong way at low speed, drive there at full cruise, and
 * ease off close-in so the arrival doesn't overshoot. Once behind the ball the
 * gap turns positive and plain PUSH re-engages with its approach control.
 */
function chaseBack(
  track: Track,
  car: DriverCar,
  sit: Situation,
  lvl: LevelTuning,
  ai: AiTuning,
  cruiseScale: number,
  boosted: boolean,
  dt: number,
  mem: DriverMemory,
): { throttle: number; steer: number } {
  const fx = forwardX(sit.bp.dir);
  const fz = forwardZ(sit.bp.dir);
  // Stage on the car's current side of the ball; flip toward road centre if
  // that side's point would sit in the guardrail (same rule as the scrum).
  let sideSign =
    Math.sign((car.x - sit.ball.x) * sit.bp.rightX + (car.z - sit.ball.z) * sit.bp.rightZ) || 1;
  if (Math.abs(sit.bp.lateral + sideSign * ai.chase.stageSide) > sit.margin - 1.5) {
    sideSign = -sideSign;
  }
  const stX = sit.ball.x - fx * ai.chase.stageBehind + sit.bp.rightX * sideSign * ai.chase.stageSide;
  const stZ = sit.ball.z - fz * ai.chase.stageBehind + sit.bp.rightZ * sideSign * ai.chase.stageSide;
  // M143c: from far away, follow the ROAD back (see tuning.ai.chase.roadDist) —
  // the straight line to the staging point can run through a guardrail.
  let aimX = stX;
  let aimZ = stZ;
  if (Math.hypot(stX - car.x, stZ - car.z) > ai.chase.roadDist) {
    const wp = track.sampleAt(Math.max(0, sit.cp.distance - ai.chase.roadLook));
    const lat = clamp(sit.cp.lateral, -ai.chase.roadLateral, ai.chase.roadLateral);
    aimX = wp.x + wp.rightX * lat;
    aimZ = wp.z + wp.rightZ * lat;
  }
  const err = shortestAngleDelta(car.heading, headingFromForward(aimX - car.x, aimZ - car.z));
  // Nose pointing badly wrong while slow → K-turn (forward pursuit would only
  // orbit a point inside the minimum turning circle). Same jam bail as PUSH.
  if (Math.abs(err) > ai.kturn.minErr && Math.abs(sit.vf) < ai.kturn.maxSpeed && mem.kjam <= 0.6) {
    if (Math.abs(sit.vf) < 0.6) mem.kjam += dt;
    else mem.kjam = 0;
    return { throttle: -ai.kturn.reverse, steer: -Math.sign(err) };
  }
  if (Math.abs(err) <= ai.kturn.minErr) mem.kjam = 0;
  const dist = Math.hypot(stX - car.x, stZ - car.z);
  // M104: a pad surge caught mid-chase-back still gets cashed (boosted cruise).
  const cruise = lvl.cruiseSpeed * (boosted ? ai.boostCruiseScale : 1);
  const target = dist > ai.chase.nearDist ? cruise * cruiseScale : ai.chase.nearSpeed;
  const turnEase = clamp(1 - Math.abs(err) / ai.hardSteer, ai.minSpeedFrac, 1);
  return {
    throttle: (target * turnEase - sit.vf) * ai.speedGain,
    steer: clamp(err * ai.steerGain, -1, 1),
  };
}

/** Is the course reasonably straight for `lookahead` past `from`? (Sampled at a
 *  few depths — a punted ball rolls far, so the whole runway must be gentle —
 *  plus a deeper guard so we never punt into a hairpin building just beyond.) */
function puntStraight(track: Track, from: number, p: typeof tuning.ai.punt): boolean {
  const d0 = track.sampleAt(Math.min(from, track.length)).dir;
  for (const frac of [0.35, 0.65, 1.0]) {
    const d = Math.min(from + p.lookahead * frac, track.length);
    if (Math.abs(shortestAngleDelta(d0, track.sampleAt(d).dir)) > p.maxBend) return false;
  }
  const deep = Math.min(from + p.deepLookahead, track.length);
  return Math.abs(shortestAngleDelta(d0, track.sampleAt(deep).dir)) <= p.deepMaxBend;
}

/**
 * Start-scrum maneuver (M17) — recovers a ball at (or knocked BEHIND) the start
 * line, where the arc-length geometry the rest of the driver runs on breaks
 * down: every projection clamps to arc ≥ 0, so pursuit aims can't see behind
 * the line, RECOVER's exit can never satisfy, and the progress watchdog goes
 * blind. The owner's telemetry showed rivals frozen here for ENTIRE races.
 *
 * Works in real space with dwell-gated phases (per-tick threshold gates were
 * measured to chatter forever):
 *   SWING    — reverse + counter-steer until the nose faces the staging point
 *              (a K-turn; forward steering can't capture a point inside the
 *              ~5-unit minimum turn radius). Jam detection bails forward.
 *   APPROACH — drive to a staging point BEHIND and BESIDE the ball (the side
 *              offset stops us ramming it deeper on the way — measured).
 *   CHARGE   — from behind it, flat-out straight through the ball, bashing it
 *              down-course. Repeat until it's clear of the zone.
 */
function scrumManeuver(
  track: Track,
  car: DriverCar,
  sit: Situation,
  mem: DriverMemory,
  ai: AiTuning,
  dt: number,
): { throttle: number; steer: number } | null {
  const fx = forwardX(sit.bp.dir);
  const fz = forwardZ(sit.bp.dir);
  const ballAhead = (sit.ball.x - car.x) * fx + (sit.ball.z - car.z) * fz;
  // Stage on the car's current side of the ball — unless that side's staging
  // point would sit in/beyond the guardrail, then flip toward road centre.
  let sideSign =
    Math.sign((car.x - sit.ball.x) * sit.bp.rightX + (car.z - sit.ball.z) * sit.bp.rightZ) || 1;
  if (Math.abs(sit.bp.lateral + sideSign * 6) > sit.margin - 1.5) sideSign = -sideSign;
  let stX = sit.ball.x - fx * 9 + sit.bp.rightX * sideSign * 6;
  let stZ = sit.ball.z - fz * 9 + sit.bp.rightZ * sideSign * 6;
  // M42: the course is CLOSED now — a barrier stands a few units behind the
  // start line. A staging point at/beyond it is physically unreachable, and
  // rotating toward a point you can't stand on cycles the swing forever
  // (exactly the death-loop this maneuver exists to break). Clamp the staging
  // point to the reachable side, car half-length + a margin inside the wall.
  const s0 = track.start;
  const sfx = forwardX(s0.dir);
  const sfz = forwardZ(s0.dir);
  const stBehind = (stX - s0.x) * sfx + (stZ - s0.z) * sfz;
  const minBehind = -(tuning.track.startBarrier - 4);
  if (stBehind < minBehind) {
    stX += sfx * (minBehind - stBehind);
    stZ += sfz * (minBehind - stBehind);
  }
  const errBall = shortestAngleDelta(car.heading, headingFromForward(sit.ball.x - car.x, sit.ball.z - car.z));
  const errStaging = shortestAngleDelta(car.heading, headingFromForward(stX - car.x, stZ - car.z));

  mem.scrumT += dt;
  const setPhase = (p: number): void => {
    mem.scrumPhase = p;
    mem.scrumT = 0;
    mem.scrumJam = 0;
  };

  // The charge chases the EXTENSION point beyond the ball, not the ball: a
  // fixed-point aim's bearing error explodes on final approach and the turn
  // radius can't follow — every charge missed by a body width (measured).
  // With the carrot past the ball, the chase line runs THROUGH it.
  const dBall = Math.hypot(sit.ball.x - car.x, sit.ball.z - car.z) || 1;
  const exX = sit.ball.x + ((sit.ball.x - car.x) / dBall) * 6;
  const exZ = sit.ball.z + ((sit.ball.z - car.z) / dBall) * 6;
  const errCharge = shortestAngleDelta(car.heading, headingFromForward(exX - car.x, exZ - car.z));
  // A charge is only legal when its strike line genuinely points DOWN-COURSE:
  // a flat `> 2.2` offset lets near-perpendicular charges through at range —
  // in hairpins those ricocheted the ball BACKWARD ~15 m per cycle, marching
  // it down the course for minutes (measured on easy/seed-123). A third is
  // the right bound: ricochets sit near cosine 0.1, honest approaches orbit
  // around 0.45-0.65 and must be allowed through (also measured).
  const chargeable = ballAhead > Math.max(2.2, dBall / 3);

  // (M104 note: loosening this gate with stuckRepeats was TRIED and reverted —
  // wide-angle charges re-broke the M17 backward-ricochet discipline, DNF'ing
  // the start-line regression. The chase-exit fix (chase.exitGap) is what
  // actually cured the Patagonia-medium dance; the strict gates stay.)
  // Already lined up tightly behind it? Skip straight to the charge.
  if (mem.scrumPhase !== 2 && chargeable && Math.abs(errBall) < 0.35) setPhase(2);

  // The swing rotates toward whatever comes NEXT: the ball itself once we're
  // legally behind it, the staging point otherwise (rotating toward a point
  // you're standing on just cycles — measured).
  const swingErr = chargeable ? errBall : errStaging;
  const behindBall = chargeable;

  if (mem.scrumPhase === 0) {
    // SWING: K-turn the nose onto the next target.
    if (Math.abs(swingErr) < (behindBall ? 0.3 : 0.5) || mem.scrumT > 2.4) {
      setPhase(1);
    } else {
      if (Math.abs(sit.vf) < 0.6) mem.scrumJam += dt;
      if (mem.scrumJam > 0.5) {
        // Jammed — M42: usually pinned on the course's new BACK wall, where a
        // reverse swing can't rotate at all (measured: 30 s of nothing). Swing
        // FORWARD instead: the opposite K-turn leg, same rotation with the
        // steer mirrored. The phase dwell above still hands over if this stalls.
        return { throttle: 0.55, steer: Math.sign(swingErr) };
      } else {
        return { throttle: -ai.kturn.reverse, steer: -Math.sign(swingErr) };
      }
    }
  }
  if (mem.scrumPhase === 1) {
    // APPROACH: drive to the staging point behind + beside the ball (speed
    // capped — a hot approach just flies past everything).
    const dStaging = Math.hypot(stX - car.x, stZ - car.z);
    if (chargeable && Math.abs(errBall) < 0.35) setPhase(2);
    else if (dStaging < 4 || mem.scrumT > 3) setPhase(0); // arrived crooked / lost → re-swing
    else if (Math.abs(errStaging) > 2.0 && Math.abs(sit.vf) < 4) setPhase(0);
    else {
      return {
        throttle: sit.vf > 12 ? 0 : 0.65,
        steer: clamp(errStaging * ai.steerGain, -1, 1),
      };
    }
  }
  if (mem.scrumPhase === 2) {
    // CHARGE: flat out along the line through the ball; re-evaluate after the
    // follow-through (or the moment the strike line stops pointing down-course).
    if (mem.scrumT > 1.4 || ballAhead < -1 || (!chargeable && mem.scrumT > 0.4)) setPhase(0);
    else return { throttle: 1, steer: clamp(errCharge * ai.steerGain * 1.5, -1, 1) };
  }
  return null; // phase just changed — let the next tick act on it
}

/**
 * RECOVER — the overshoot fix the owner asked for: brake first, then back STRAIGHT
 * up-course (never turn into the ball). Two phases:
 *   1. Still rolling forward → brake hard while steering to face straight
 *      down-course, so the reverse will run in a clean line up the track.
 *   2. Actually rolling backward → reverse dead-straight (steer 0). This opens a
 *      long run-up behind the ball; PUSH then curves onto the ball's lane and
 *      strikes it head-on. (A straight reverse alone can't re-acquire a ball off
 *      to one side — the run-up is what lets PUSH swing back onto its lane.)
 * Steering INVERTS once actually rolling backward (arcadeCar.ts
 * `reverseSign = vf < 0 ? -1 : 1`); phase 2 steers 0 so that inversion is moot.
 */
function recoverBehavior(sit: Situation, ai: AiTuning): { throttle: number; steer: number } {
  // M104 guard: RECOVER's whole plan assumes the car FACES roughly down-course
  // (the normal overshoot posture — brake, then back straight up-course).
  // Entered facing UP-course (e.g. fresh off a chase-back U-turn), "reverse"
  // moves the car DOWN-course, away from the ball — the measured ping-pong
  // half of the Patagonia-medium stall. Facing the wrong way, drive FORWARD
  // past the ball instead: forward IS up-course here, which opens the run-up
  // exactly like a completed reverse would.
  if (Math.abs(sit.downCourseErr) > 2.0) {
    return { throttle: 0.6, steer: 0 };
  }
  // Regulate to a slow reverse (brakes hard from forward first) — a full-throttle
  // reverse just builds wild backward speed and arcs.
  const throttle = clamp((ai.fsm.reverseSpeed - sit.vf) * ai.speedGain, -1, 1);
  if (sit.vf < -ai.fsm.reverseEps) {
    return { throttle, steer: 0 }; // actually reversing: dead-straight back, no circling
  }
  // Braking (still rolling forward, normal steering): turn to face down-course so the
  // back-up runs up-course in a straight line.
  return { throttle, steer: clamp(sit.downCourseErr * ai.steerGain, -1, 1) };
}
